import React from 'react';
import { useWizard } from '../../context/WizardContext';
import NavigationButtons from '../shared/NavigationButtons';
import { AlertTriangle, Shield, Info } from 'lucide-react';

const PARTICIPANT_RISKS = [
  { id: 'physical_discomfort', label: 'Physical discomfort or fatigue' },
  { id: 'physical_harm', label: 'Risk of physical harm or injury' },
  { id: 'psychological_distress', label: 'Psychological distress or anxiety' },
  { id: 'embarrassment', label: 'Embarrassment or loss of dignity' },
  { id: 'sensitive_topics', label: 'Discussion of sensitive topics' },
  { id: 'privacy_breach', label: 'Risk of privacy breach' },
  { id: 'stigma', label: 'Social stigma or discrimination' },
  { id: 'time_burden', label: 'Significant time burden' },
  { id: 'financial_cost', label: 'Financial cost to participant' },
  { id: 'deception', label: 'Use of deception' },
  { id: 'power_imbalance', label: 'Power imbalance with researcher' },
  { id: 'coercion', label: 'Risk of perceived coercion' },
  { id: 'minimal', label: 'Minimal risk (no more than everyday life)' },
  { id: 'none', label: 'No identifiable risks' }
];

function D8_RiskAssessment() {
  const { dataE, updateDataRaw, nextStep, previousStep } = useWizard();
  const riskAssessment = dataE.riskAssessment;

  const updateRiskAssessment = (field, value) => {
    updateDataRaw({
      'wizardE.riskAssessment': { ...riskAssessment, [field]: value }
    });
  };

  const toggleRisk = (riskId) => {
    const currentRisks = riskAssessment.participantRisks || [];
    
    if (riskId === 'none') {
      updateRiskAssessment('participantRisks', ['none']);
      return;
    }
    
    let newRisks = currentRisks.filter(r => r !== 'none');
    
    if (newRisks.includes(riskId)) {
      newRisks = newRisks.filter(r => r !== riskId);
    } else {
      newRisks = [...newRisks, riskId];
    }
    
    updateRiskAssessment('participantRisks', newRisks);
  };

  const hasSignificantRisks = riskAssessment.participantRisks?.some(
    r => ['physical_harm', 'psychological_distress', 'deception', 'sensitive_topics'].includes(r)
  );

  const minimalOrNoRisk = riskAssessment.participantRisks?.includes('none') || 
                          riskAssessment.participantRisks?.includes('minimal');

  const isComplete = riskAssessment.participantRisks.length > 0 &&
                     (minimalOrNoRisk || riskAssessment.participantMitigations);

  return (
    <div className="wizard-step">
      <div className="step-header">
        <AlertTriangle className="step-icon" size={32} />
        <div>
          <h2>Step 8: Risk Assessment</h2>
          <p className="step-description">
            Identify potential risks to participants and researchers, and describe how you will mitigate them.
          </p>
        </div>
      </div>

      <div className="form-section">
        <h3>Risks to Participants</h3>

        <div className="info-box info">
          <Info size={20} />
          <div>
            <strong>Assessing Risk</strong>
            <p>
              Consider any potential harm, discomfort, or negative consequences that could arise from 
              participating in your research. Even minimal risk studies should identify and address 
              potential issues.
            </p>
          </div>
        </div>

        <div className="form-group">
          <label>What risks might participants face? *</label>
          <div className="checkbox-grid">
            {PARTICIPANT_RISKS.map(risk => (
              <label key={risk.id} className="checkbox-option">
                <input
                  type="checkbox"
                  checked={riskAssessment.participantRisks?.includes(risk.id) || false}
                  onChange={() => toggleRisk(risk.id)}
                />
                <span>{risk.label}</span>
              </label>
            ))}
          </div>
        </div>

        {hasSignificantRisks && (
          <div className="info-box warning">
            <AlertTriangle size={20} />
            <div>
              <strong>Higher Risk Research</strong>
              <p>
                You have identified significant risks. You must provide detailed mitigation strategies 
                and may require additional ethical review.
              </p>
            </div>
          </div>
        )}

        {!minimalOrNoRisk && (
          <div className="form-group">
            <label>How will you mitigate these risks? *</label>
            <textarea
              value={riskAssessment.participantMitigations}
              onChange={(e) => updateRiskAssessment('participantMitigations', e.target.value)}
              placeholder="Describe the specific steps you will take to minimize each identified risk and protect participants"
              rows={6}
            />
          </div>
        )}
      </div>

      <div className="form-section">
        <h3>Risks to Researchers</h3>

        <div className="form-group">
          <label>What risks might researchers face?</label>
          <textarea
            value={riskAssessment.researcherRisks}
            onChange={(e) => updateRiskAssessment('researcherRisks', e.target.value)}
            placeholder="Consider risks such as lone working, visiting unfamiliar locations, emotional impact of sensitive topics, physical risks during fieldwork"
            rows={4}
          />
        </div>

        {riskAssessment.researcherRisks && (
          <div className="form-group">
            <label>How will you mitigate researcher risks?</label>
            <textarea
              value={riskAssessment.researcherMitigations}
              onChange={(e) => updateRiskAssessment('researcherMitigations', e.target.value)}
              placeholder="Describe safety protocols, check-in procedures, supervision arrangements, or support available"
              rows={4}
            />
          </div>
        )}
      </div>

      <div className="form-section">
        <h3>Incident Response</h3>

        <div className="form-group">
          <label>What will you do if an adverse event occurs?</label>
          <textarea
            value={riskAssessment.incidentResponse}
            onChange={(e) => updateRiskAssessment('incidentResponse', e.target.value)}
            placeholder="Describe your procedure for handling adverse events, participant distress, or unexpected problems (e.g., stopping procedures, providing support, reporting requirements)"
            rows={5}
          />
          <small className="field-help">
            Include information about signposting to support services if participants become distressed.
          </small>
        </div>
      </div>

      <NavigationButtons
        onPrevious={previousStep}
        onNext={isComplete ? nextStep : null}
        disableNext={!isComplete}
        nextLabel="Continue to Special Considerations"
      />
    </div>
  );
}

export default D8_RiskAssessment;
